import React, { useContext } from 'react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client/react/hooks';
import { Link } from 'react-router-dom';

import Image from 'lib/images/image-view';
import SVGIcon from 'lib/images/svg-icon/svg-icon';

import { displayedName } from 'four-nets/user/utils';
import IconUser from 'four-nets/icons/svg/icon_user-2.svg';
import IconLike from 'four-nets/icons/svg/icon_like-4.svg';
import IconPhotoblogs from 'four-nets/icons/svg/icon_photo-outline-2.svg';
import { ViewerContext } from 'four-nets/user/viewer/viewer';

import query, { StaffpicksQuery, StaffpicksQueryVariables } from './staffpicks.graphql';
import { PostType } from './staffpicks.graphql.types';
import defaultStyles from './staffpicks.scss';

export interface Props {
    styles: typeof defaultStyles;
    first?: number;
}

const Staffpicks: React.FC<Props> = React.memo((props: Props) => {
    const { styles } = props;

    const { viewer } = useContext(ViewerContext);
    const { data } = useQuery<StaffpicksQuery, StaffpicksQueryVariables>(query, {
        variables: {
            first: props.first || 5,
            auth: Boolean(viewer),
        },
        fetchPolicy: 'cache-first',
    });

    if (!data || !data.feed || !data.feed.edges || !data.feed.edges.length) {
        return null;
    }

    const posts = data.feed.edges!;

    return (
        <div className={styles.wrapper}>
            <Link to={'/blogs/staff-picks/'} className={styles.wrapperTitle}>
                <FormattedMessage id='STAFFPICKS_BADGE' />
            </Link>
            {posts.map(edge => {
                if (!edge?.node?.images?.edges.length && !edge?.node?.titleImage) {
                    return null;
                }

                const { id, slug, title, user, likesCount, imagesCount, images, titleImage, postType } = edge.node;

                const link = `/blog/${user && user.username}/${
                    postType === 'POST_ALBUM' ? 'album' : 'article'
                }/${slug}/`;

                const albumRegex = /\/blog\/[a-zA-Z0-9\.\_\-]+\/album/;
                const isAlbum = albumRegex.test(link);

                const albumPhotoFile = images?.edges.length ? images.edges[0]?.node?.photoFile! : titleImage!;

                return (
                    <div className={styles.row} key={id}>
                        <div className={styles.image}>
                            {isAlbum ? (
                                <a href={link}>
                                    <Image
                                        sizes={{ xs: 150 }}
                                        aspectFill={true}
                                        srcSet={albumPhotoFile.srcset}
                                        src={albumPhotoFile.src}
                                        alt={title}
                                        linkify={false}
                                    />
                                </a>
                            ) : (
                                <Link to={link} className={styles.link}>
                                    <Image
                                        sizes={{ xs: 150 }}
                                        aspectFill={true}
                                        srcSet={albumPhotoFile.srcset}
                                        src={albumPhotoFile.src}
                                        alt={title}
                                        linkify={false}
                                    />
                                </Link>
                            )}
                        </div>
                        <div className={styles.column}>
                            <Link to={link} className={styles.link}>
                                <p className={styles.postTitle}>{title}</p>
                            </Link>
                            <div className={styles.userInfo}>
                                <Link to={`/blog/${user && user.username}/`} className={styles.usernameLink}>
                                    <SVGIcon style={{ image: styles.userIcon }} src={IconUser} />
                                    {user && displayedName(user)}
                                </Link>
                            </div>
                            <div className={styles.postInfo}>
                                <SVGIcon style={{ image: styles.infoIcon }} src={IconLike} />
                                <span className={styles.infoText}>{likesCount}</span>
                                {postType === 'POST_ALBUM' ? (
                                    <>
                                        <SVGIcon style={{ image: styles.infoIcon }} src={IconPhotoblogs} />
                                        <span className={styles.infoText}>{imagesCount}</span>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

export default Styled(defaultStyles)(Staffpicks);
